import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, Clock, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useSubscriptionStatus } from '../context/SubscriptionContext';
import { buildExpiryNotices } from '../lib/expiry-notices';

const SESSION_KEY = 'expiry_modal_shown';

export function ExpiryLoginModal() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { status, loading: statusLoading } = useSubscriptionStatus();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (authLoading || statusLoading) return;
    if (!isAuthenticated || !status || user?.isAdmin) return;

    const alreadyShown = sessionStorage.getItem(SESSION_KEY) === 'true';
    if (alreadyShown) return;

    const notices = buildExpiryNotices(status);
    if (notices.length === 0) return;

    setOpen(true);
    sessionStorage.setItem(SESSION_KEY, 'true');
  }, [authLoading, statusLoading, isAuthenticated, status]);

  const handleClose = () => setOpen(false);

  if (!status) return null;
  const notices = buildExpiryNotices(status);
  if (notices.length === 0) return null;

  const hasExpired = notices.some((n) => n.severity === 'expired');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {hasExpired ? 'Abonnement abgelaufen' : 'Abonnement läuft bald ab'}
          </DialogTitle>
          <DialogDescription>
            {hasExpired
              ? 'Bitte verlängere dein Abonnement, um alle Funktionen weiter nutzen zu können.'
              : 'Verlängere rechtzeitig, damit deine Funktionen ohne Unterbrechung erhalten bleiben.'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 mt-2">
          {notices.map((notice, idx) => {
            const isExpired = notice.severity === 'expired';
            const Icon = isExpired ? AlertTriangle : Clock;
            const boxClasses = isExpired
              ? 'bg-red-50 border-red-200 text-red-900'
              : 'bg-amber-50 border-amber-200 text-amber-900';
            const iconClasses = isExpired ? 'text-red-600' : 'text-amber-600';
            return (
              <div key={idx} className={`flex items-start gap-3 rounded-lg border px-3 py-2 ${boxClasses}`}>
                <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconClasses}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{notice.title}</p>
                  <p className="text-sm opacity-90">{notice.message}</p>
                </div>
              </div>
            );
          })}
          <Button asChild variant="default" className="mt-2">
            <Link to="/preise" onClick={handleClose}>
              Jetzt verlängern
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
          <Button variant="ghost" onClick={handleClose}>Später erinnern</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
